import React, { useState } from "react";
import { Table, Button, Spinner } from "react-bootstrap";
import {
  getPdfByBlobName,
  getReferralBlobFileByDate,
} from "../../../services/ReferralService";
import { ReferralBlob } from "../../../types/IReferral";
import { useOktaAuth } from "@okta/okta-react";
import "./Referral.css";
import CustomAlertModal from "../../common/CustomAlertModal";
import { getFormattedMaxDate } from "../../../helpers/DateHelper";
const AllReferralsPage: React.FC = () => {
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken;
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [files, setFiles] = useState<ReferralBlob[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState<string>("");

  const fetchFiles = async () => {
    setError("");
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date.");
      return;
    }
    setSearchPerformed(true);
    setLoading(true);

    try {
      const blobFiles = await getReferralBlobFileByDate(
        startDate,
        endDate,
        accessToken
      );
      setFiles(blobFiles);
    } catch (err) {
      setError("There was an error fetching the referrals. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewPdf = async (blobName: string) => {
    setError("");
    if (loadingPdf) {
      return;
    }
    setLoadingPdf(blobName);
    try {
      const pdfBlobUrl = await getPdfByBlobName(blobName, accessToken);
      window.open(pdfBlobUrl, "_blank");
    } catch (error) {
      setError("There was an error showing the referral. Please try again.");
      console.error("Error opening PDF.");
    } finally {
      setLoadingPdf(null);
    }
  };

  return (
    <div className="container referral-container">
      <div className="d-flex mb-4">
        <h2 className="heading-main">All Referrals</h2>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchFiles();
        }}
      >
        <div className="referral-search-container">
          <div className="date-range">
            <div className="date-input">
              <label htmlFor="start-date" className="label-start">
                Start Date
              </label>
              <input
                id="start-date"
                type="date"
                className="form-control date-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={getFormattedMaxDate(new Date())}
                required
              />
            </div>
            <div className="date-input">
              <label htmlFor="end-date" className="label-end">
                End Date
              </label>
              <input
                id="end-date"
                type="date"
                className="form-control date-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                max={getFormattedMaxDate(new Date())}
                required
              />
            </div>
          </div>

          <div className="action-buttons d-flex justify-content-end mt-0">
            <button type="submit" className="btn-search" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" role="status" />
              ) : (
                "Search"
              )}
            </button>
          </div>
        </div>
      </form>

      {!loading && files.length === 0 && searchPerformed && (
        <div className="text-center text-muted mt-4">No files found</div>
      )}

      {!loading && files.length > 0 && (
        <div className="table-container">
          <Table id="responsive-table-referrals" striped hover responsive>
            <thead className="table-header">
              <tr>
                <th className="px-2 py-3 test">File Name</th>
                <th className="px-2 py-3">Created Date</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="px-1">
              {files.map((file) => (
                <tr key={file.blobName}>
                  <td className="px-2">{file.blobName}</td>
                  <td className="px-2">{file.createdOn?.split("T")[0]}</td>
                  <td className="px-2">
                    <button
                      onClick={() => viewPdf(file.blobName)}
                      disabled={loadingPdf === file.blobName}
                      className="btn-view"
                    >
                      {loadingPdf === file.blobName ? (
                        <Spinner animation="border" size="sm" role="status" />
                      ) : (
                        "View"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {error && (
        <CustomAlertModal
          show={error !== ""}
          handleClose={() => {
            setError("");
          }}
          body={error}
        />
      )}
    </div>
  );
};

export default AllReferralsPage;
